import { Fragment, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import Button from '../Button'
import {
    AccountRecord,
    ContentView,
    SocialMediaServiceEnum
} from '../../api/client'
import CrossProfileEngageSelectionComponent from '../actions/CrossProfileEngageSelectionComponent'
import { getAccountDisplayName } from '../../util/account-utils'

type Props = {
    onReply: (message: string) => Promise<void> | void
    sendingReply: boolean
    initialReply: string
    content: ContentView
    canUseCrossProfileEngage?: boolean
    currentAccount: AccountRecord
    selectedAccountId?: number
    defaultAccountId?: number
    selectedAccountLabel?: string
    onAccountSelect?: (account: AccountRecord) => void
    onMakeDefault?: (account: AccountRecord) => void
    replyDisabled?: boolean
    replyDisabledMessage?: string
}

type FormData = {
    comment: string
}

/**
 * Component to display a text area for a user to reply to a message.
 */
export default function NewReply(props: Props) {
    const {
        onReply,
        sendingReply,
        initialReply,
        content,
        canUseCrossProfileEngage,
        currentAccount,
        selectedAccountId,
        defaultAccountId,
        selectedAccountLabel,
        onAccountSelect,
        onMakeDefault,
        replyDisabled = false,
        replyDisabledMessage
    } = props

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors }
    } = useForm<FormData>({
        defaultValues: { comment: initialReply }
    })

    useEffect(() => {
        setValue('comment', initialReply)
    }, [initialReply, setValue])

    const onSubmit = async (data: FormData) => {
        await onReply(data.comment)
    }

    const TIKTOK_REPLY_CHARACTER_LIMIT = 150
    const X_REPLY_CHARACTER_LIMIT = 280
    const INSTAGRAM_REPLY_CHARACTER_LIMIT = 2200
    const FACEBOOK_REPLY_CHARACTER_LIMIT = 8000

    const maxReplyLength = (() => {
        switch (content.account.socialMediaServiceShortName?.toLowerCase()) {
            case SocialMediaServiceEnum.Tiktokuser.toLowerCase():
            case SocialMediaServiceEnum.Tiktokad.toLowerCase():
                return TIKTOK_REPLY_CHARACTER_LIMIT
            case SocialMediaServiceEnum.Twitter.toLowerCase():
                return X_REPLY_CHARACTER_LIMIT
            case SocialMediaServiceEnum.Instagram.toLowerCase():
                return INSTAGRAM_REPLY_CHARACTER_LIMIT
            case SocialMediaServiceEnum.Facebook.toLowerCase():
                return FACEBOOK_REPLY_CHARACTER_LIMIT
            default:
                return 150 // Default value if no match - this should never happen
        }
    })()

    const comment = watch('comment', initialReply)
    const charactersUsed = comment?.length || 0

    const showCrossProfileDropdown = Boolean(canUseCrossProfileEngage && onAccountSelect)
    const currentAccountLabel = getAccountDisplayName(currentAccount)
    const accountLabel =
        selectedAccountLabel ??
        (currentAccountLabel === 'Unknown account' ? 'this account' : currentAccountLabel)

    const usingAlternateAccount = Boolean(
        showCrossProfileDropdown &&
            selectedAccountId !== undefined &&
            selectedAccountId !== currentAccount.id
    )

    const replyButtonLabel = usingAlternateAccount ? `Reply as ${accountLabel}` : 'Reply'
    const isReplyDisabled = replyDisabled || sendingReply

    const tooltipProps = replyDisabled && replyDisabledMessage
        ? {
              'data-tooltip-id': 'tooltip-id',
              'data-tooltip-content': replyDisabledMessage
          }
        : {}

    return (
        <div className="flex items-start space-x-4 w-full">
            <div className="min-w-0 flex flex-col w-full">
                <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                    <div className="mt-10 ml-8 pr-8 opacity-100 relative w-full">
                        <label htmlFor="comment" className="sr-only">
                            Add your response
                        </label>
                        <textarea
                            rows={5}
                            id="comment"
                            className="w-full border border-ink-200 shadow-md rounded-lg  resize-none  text-ink-900 placeholder:text-ink-400 focus:ring-0"
                            placeholder="Reply to this message..."
                            maxLength={maxReplyLength}
                            {...register('comment', {
                                required: 'Comment is required',
                                maxLength: {
                                    value: maxReplyLength,
                                    message: `Comment cannot exceed ${maxReplyLength} characters`
                                }
                            })}
                        />
                    </div>

                    <div className="flex flex-row justify-between ml-8 mt-2 items-baseline">
                        {errors.comment && <p className="text-red-500">{errors.comment.message}</p>}
                        <div></div>
                        <div className="flex flex-row items-baseline">
                            <div className="text-ink-700 text-sm mr-4">
                                {`${charactersUsed}/${maxReplyLength}`}
                            </div>
                            {showCrossProfileDropdown ? (
                                <Popover className="relative">
                                    {({ open, close }) => (
                                        <>
                                            <div
                                                className={clsx(
                                                    'inline-flex h-10 items-center overflow-hidden rounded-md border border-arwen-600 bg-arwen-600 text-white shadow-sm transition-colors duration-150 ease-linear',
                                                    isReplyDisabled
                                                        ? 'cursor-not-allowed opacity-70'
                                                        : 'hover:bg-arwen-700'
                                                )}
                                                {...tooltipProps}
                                            >
                                                <button
                                                    type="submit"
                                                    className={clsx(
                                                        'flex h-full items-center justify-center px-4 text-sm font-semibold uppercase tracking-wide',
                                                        isReplyDisabled
                                                            ? 'cursor-not-allowed'
                                                            : 'hover:bg-arwen-700'
                                                    )}
                                                    disabled={isReplyDisabled}
                                                >
                                                    {replyButtonLabel}
                                                </button>
                                                <span aria-hidden="true" className="h-full w-px bg-white/40" />
                                                <PopoverButton
                                                    type="button"
                                                    className={clsx(
                                                        'flex h-full w-10 items-center justify-center transition-colors duration-150 ease-linear focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
                                                        isReplyDisabled
                                                            ? 'cursor-not-allowed'
                                                            : 'hover:bg-arwen-700'
                                                    )}
                                                    disabled={isReplyDisabled}
                                                    aria-label="Open cross profile reply options"
                                                >
                                                    <ChevronDownIcon
                                                        className={clsx(
                                                            'h-4 w-4 transition-transform duration-150 ease-linear',
                                                            open ? 'rotate-180' : undefined
                                                        )}
                                                    />
                                                </PopoverButton>
                                            </div>

                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-100"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-75"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <PopoverPanel className="absolute right-0 z-20 mt-2 w-96 rounded-lg bg-white p-4 text-sm text-ink-600 shadow-lg ring-1 ring-black ring-opacity-5">
                                                    <CrossProfileEngageSelectionComponent
                                                        currentAccount={currentAccount}
                                                        selectedAccountId={selectedAccountId}
                                                        defaultAccountId={defaultAccountId}
                                                        onAccountSelect={(account) => {
                                                            onAccountSelect?.(account)
                                                            close()
                                                        }}
                                                        onMakeDefault={onMakeDefault}
                                                        onRequestClose={() => close()}
                                                        mode="reply"
                                                    />
                                                </PopoverPanel>
                                            </Transition>
                                        </>
                                    )}
                                </Popover>
                            ) : (
                                <div {...tooltipProps}>
                                    <Button
                                        primary={true}
                                        disabled={isReplyDisabled}
                                        onClick={handleSubmit(onSubmit)}
                                        text={replyButtonLabel}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
